import { useEffect } from 'react';
import { Header } from '../components/Header';
import { FooterSection } from '../components/FooterSection';
import { Toaster } from '../components/ui/sonner';
import { useLanguage } from '../context/LanguageContext';
import { ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export default function PrivacyPolicy() {
  const { lang } = useLanguage();
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const content = lang === 'de' ? deContent : enContent;

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <Header />
      <div className="pt-28 pb-20 max-w-4xl mx-auto px-6">
        <button
          onClick={() => navigate('/')}
          className="flex items-center gap-2 text-[#C9A84C] text-sm font-medium mb-8 hover:text-[#D4B85A] transition-colors duration-300"
        >
          <ArrowLeft size={16} />
          {lang === 'de' ? 'Zurück zur Startseite' : 'Back to Homepage'}
        </button>

        <h1 className="text-4xl sm:text-5xl font-bold text-[#F5F0E8] font-heading mb-4">
          {content.title}
        </h1>
        <p className="text-[#8A8680] text-sm mb-12">
          {content.lastUpdated}
        </p>

        <div className="space-y-10">
          {content.sections.map((section, i) => (
            <div key={i}>
              <h2 className="text-xl font-bold text-[#C9A84C] mb-4 font-heading">
                {section.title}
              </h2>
              <div className="text-[#9A9590] text-base leading-relaxed space-y-3">
                {section.paragraphs.map((p, j) => (
                  <p key={j}>{p}</p>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
      <FooterSection />
      <Toaster position="top-center" theme="dark" />
    </div>
  );
}

const enContent = {
  title: 'Privacy Policy',
  lastUpdated: 'Last updated: August 2025',
  sections: [
    {
      title: '1. Controller',
      paragraphs: [
        'The controller responsible for data processing on this website is: Next Level, represented by Domenico Gurzi, Marbella, Spain. Email: info@next-level.es.',
        'When we refer to "Next Level", "we", "us" or "our" in this privacy policy, we mean the entity above.'
      ]
    },
    {
      title: '2. Overview of Data Processing',
      paragraphs: [
        'We take the protection of your personal data very seriously. We process personal data in compliance with the European General Data Protection Regulation (GDPR), the applicable EU member state data protection laws, and other relevant data protection regulations.',
        'Personal data is only collected on this website to the extent technically necessary. Under no circumstances will the collected data be sold to third parties.'
      ]
    },
    {
      title: '3. Data We Collect',
      paragraphs: [
        'When you visit our website, our web server temporarily stores the IP address, date and time of access, requested page/file, HTTP status code, browser type, operating system, and referrer URL in server log files. This data is used exclusively for ensuring secure operation and is deleted after 30 days.',
        'When you use our Early Access / contact form, we collect: your name, email address, your role (Player, Parent, Coach, Club, Scout, Medical Staff), your optional message, and your newsletter consent preference. This data is processed on the basis of Art. 6(1)(a) GDPR (consent) and Art. 6(1)(b) GDPR (pre-contractual measures).'
      ]
    },
    {
      title: '4. Purpose of Data Processing',
      paragraphs: [
        'We process your data for the following purposes: (a) to respond to your inquiry and provide you with information about our services, (b) to send you updates about our platform launch and programs (only with your explicit consent), (c) to ensure the technical functionality and security of our website, (d) to comply with legal obligations.'
      ]
    },
    {
      title: '5. Legal Basis for Processing',
      paragraphs: [
        'The processing of your personal data is based on: Art. 6(1)(a) GDPR – your consent (e.g., newsletter signup), Art. 6(1)(b) GDPR – performance of a contract or pre-contractual measures (e.g., responding to your inquiry), Art. 6(1)(f) GDPR – our legitimate interests (e.g., website security, analytics).'
      ]
    },
    {
      title: '6. Data Storage and Retention',
      paragraphs: [
        'Your personal data is stored on secure servers. We retain your Early Access form data until you request deletion or until the data is no longer needed for the purpose it was collected. Server log files are automatically deleted after 30 days.',
        'If you have given consent for newsletter communications, we will retain your email address until you withdraw your consent.'
      ]
    },
    {
      title: '7. Data Sharing with Third Parties',
      paragraphs: [
        'We do not sell, rent, or trade your personal data. We may share data with: trusted service providers who process data on our behalf (e.g., hosting provider) under strict data processing agreements, and public authorities if required by law.',
        'Any data transfer to countries outside the EU/EEA is carried out in compliance with appropriate safeguards as required by the GDPR (e.g., Standard Contractual Clauses).'
      ]
    },
    {
      title: '8. Your Rights (GDPR)',
      paragraphs: [
        'Under the GDPR, you have the following rights regarding your personal data:',
        'Right of Access (Art. 15 GDPR): You have the right to request information about whether and which personal data we process about you.',
        'Right to Rectification (Art. 16 GDPR): You can request the correction of inaccurate personal data.',
        'Right to Erasure (Art. 17 GDPR): You can request the deletion of your personal data ("right to be forgotten").',
        'Right to Restriction of Processing (Art. 18 GDPR): You can request restriction of processing under certain conditions.',
        'Right to Data Portability (Art. 20 GDPR): You have the right to receive your data in a structured, commonly used, and machine-readable format.',
        'Right to Object (Art. 21 GDPR): You can object to processing based on legitimate interests at any time.',
        'Right to Withdraw Consent (Art. 7(3) GDPR): You can withdraw any given consent at any time with effect for the future.',
        'To exercise these rights, contact us at: info@next-level.es.'
      ]
    },
    {
      title: '9. Cookies',
      paragraphs: [
        'This website uses only technically necessary cookies (e.g., language preference stored in localStorage). We do not use tracking cookies, advertising cookies, or any third-party analytics tools that would require consent under the ePrivacy Directive.',
        'Your language preference is stored locally in your browser (localStorage) and is not transmitted to our servers.'
      ]
    },
    {
      title: '10. Data Security',
      paragraphs: [
        'We implement appropriate technical and organizational security measures to protect your personal data against accidental or unlawful destruction, loss, alteration, unauthorized disclosure, or access. Data transmission to our website is encrypted using TLS/SSL.'
      ]
    },
    {
      title: '11. Right to Lodge a Complaint',
      paragraphs: [
        'If you believe that the processing of your personal data violates data protection law, you have the right to lodge a complaint with a supervisory authority. In Spain, the competent authority is the Agencia Española de Protección de Datos (AEPD), www.aepd.es.'
      ]
    },
    {
      title: '12. Changes to This Privacy Policy',
      paragraphs: [
        'We reserve the right to update this privacy policy to reflect changes in legal requirements or our data processing practices. The current version is always available on this page.'
      ]
    },
    {
      title: '13. Contact',
      paragraphs: [
        'If you have questions regarding data protection or wish to exercise your rights, please contact us at: info@next-level.es.'
      ]
    },
  ]
};

const deContent = {
  title: 'Datenschutzerklärung',
  lastUpdated: 'Zuletzt aktualisiert: August 2025',
  sections: [
    {
      title: '1. Verantwortlicher',
      paragraphs: [
        'Verantwortlich für die Datenverarbeitung auf dieser Website ist: Next Level, vertreten durch Domenico Gurzi, Marbella, Spanien. E-Mail: info@next-level.es.',
        'Wenn wir in dieser Datenschutzerklärung von „Next Level", „wir" oder „uns" sprechen, meinen wir die oben genannte Stelle.'
      ]
    },
    {
      title: '2. Übersicht der Datenverarbeitung',
      paragraphs: [
        'Wir nehmen den Schutz Ihrer persönlichen Daten sehr ernst. Wir verarbeiten personenbezogene Daten im Einklang mit der Europäischen Datenschutz-Grundverordnung (DSGVO), den geltenden Datenschutzgesetzen der EU-Mitgliedstaaten sowie weiteren einschlägigen Datenschutzvorschriften.',
        'Personenbezogene Daten werden auf dieser Website nur im technisch notwendigen Umfang erhoben. Die erhobenen Daten werden unter keinen Umständen an Dritte verkauft.'
      ]
    },
    {
      title: '3. Welche Daten wir erheben',
      paragraphs: [
        'Beim Besuch unserer Website speichert unser Webserver temporär die IP-Adresse, Datum und Uhrzeit des Zugriffs, die angeforderte Seite/Datei, HTTP-Statuscode, Browsertyp, Betriebssystem und Referrer-URL in Server-Logdateien. Diese Daten dienen ausschließlich der sicheren Betriebsführung und werden nach 30 Tagen gelöscht.',
        'Bei Nutzung unseres Early-Access-/Kontaktformulars erheben wir: Ihren Namen, Ihre E-Mail-Adresse, Ihre Rolle (Spieler, Eltern, Coach, Club, Scout, Medical Staff), Ihre optionale Nachricht und Ihre Newsletter-Einwilligung. Diese Daten werden auf Grundlage von Art. 6 Abs. 1 lit. a DSGVO (Einwilligung) und Art. 6 Abs. 1 lit. b DSGVO (vorvertragliche Maßnahmen) verarbeitet.'
      ]
    },
    {
      title: '4. Zweck der Datenverarbeitung',
      paragraphs: [
        'Wir verarbeiten Ihre Daten zu folgenden Zwecken: (a) Beantwortung Ihrer Anfrage und Information über unsere Dienstleistungen, (b) Zusendung von Updates zum Plattform-Launch und Programmen (nur mit Ihrer ausdrücklichen Einwilligung), (c) Sicherstellung der technischen Funktionalität und Sicherheit unserer Website, (d) Erfüllung gesetzlicher Verpflichtungen.'
      ]
    },
    {
      title: '5. Rechtsgrundlage der Verarbeitung',
      paragraphs: [
        'Die Verarbeitung Ihrer personenbezogenen Daten basiert auf: Art. 6 Abs. 1 lit. a DSGVO – Ihre Einwilligung (z.B. Newsletter-Anmeldung), Art. 6 Abs. 1 lit. b DSGVO – Vertragserfüllung oder vorvertragliche Maßnahmen (z.B. Beantwortung Ihrer Anfrage), Art. 6 Abs. 1 lit. f DSGVO – unsere berechtigten Interessen (z.B. Website-Sicherheit).'
      ]
    },
    {
      title: '6. Datenspeicherung und -aufbewahrung',
      paragraphs: [
        'Ihre personenbezogenen Daten werden auf sicheren Servern gespeichert. Wir bewahren Ihre Early-Access-Formulardaten auf, bis Sie die Löschung verlangen oder die Daten für den Zweck, zu dem sie erhoben wurden, nicht mehr benötigt werden.',
        'Wenn Sie in die Newsletter-Kommunikation eingewilligt haben, speichern wir Ihre E-Mail-Adresse, bis Sie Ihre Einwilligung widerrufen.'
      ]
    },
    {
      title: '7. Datenweitergabe an Dritte',
      paragraphs: [
        'Wir verkaufen, vermieten oder handeln nicht mit Ihren personenbezogenen Daten. Wir können Daten teilen mit: vertrauenswürdigen Dienstleistern, die Daten in unserem Auftrag verarbeiten (z.B. Hosting-Anbieter) unter strengen Auftragsverarbeitungsverträgen, und öffentlichen Behörden, sofern gesetzlich vorgeschrieben.',
        'Jede Datenübermittlung in Länder außerhalb der EU/des EWR erfolgt unter Einhaltung geeigneter Garantien gemäß DSGVO (z.B. Standardvertragsklauseln).'
      ]
    },
    {
      title: '8. Ihre Rechte (DSGVO)',
      paragraphs: [
        'Nach der DSGVO haben Sie folgende Rechte bezüglich Ihrer personenbezogenen Daten:',
        'Auskunftsrecht (Art. 15 DSGVO): Sie haben das Recht zu erfahren, ob und welche personenbezogenen Daten wir über Sie verarbeiten.',
        'Recht auf Berichtigung (Art. 16 DSGVO): Sie können die Korrektur unrichtiger Daten verlangen.',
        'Recht auf Löschung (Art. 17 DSGVO): Sie können die Löschung Ihrer personenbezogenen Daten verlangen ("Recht auf Vergessenwerden").',
        'Recht auf Einschränkung der Verarbeitung (Art. 18 DSGVO): Sie können unter bestimmten Voraussetzungen die Einschränkung der Verarbeitung verlangen.',
        'Recht auf Datenübertragbarkeit (Art. 20 DSGVO): Sie haben das Recht, Ihre Daten in einem strukturierten, gängigen und maschinenlesbaren Format zu erhalten.',
        'Widerspruchsrecht (Art. 21 DSGVO): Sie können der Verarbeitung auf Basis berechtigter Interessen jederzeit widersprechen.',
        'Recht auf Widerruf der Einwilligung (Art. 7 Abs. 3 DSGVO): Sie können eine erteilte Einwilligung jederzeit mit Wirkung für die Zukunft widerrufen.',
        'Zur Ausübung dieser Rechte kontaktieren Sie uns unter: info@next-level.es.'
      ]
    },
    {
      title: '9. Cookies',
      paragraphs: [
        'Diese Website verwendet ausschließlich technisch notwendige Cookies (z.B. Spracheinstellung im localStorage). Wir verwenden keine Tracking-Cookies, Werbe-Cookies oder Drittanbieter-Analysetools, die eine Einwilligung nach der ePrivacy-Richtlinie erfordern würden.',
        'Ihre Spracheinstellung wird lokal in Ihrem Browser (localStorage) gespeichert und nicht an unsere Server übertragen.'
      ]
    },
    {
      title: '10. Datensicherheit',
      paragraphs: [
        'Wir setzen angemessene technische und organisatorische Sicherheitsmaßnahmen ein, um Ihre personenbezogenen Daten gegen zufällige oder unrechtmäßige Zerstörung, Verlust, Veränderung, unbefugte Offenlegung oder Zugang zu schützen. Die Datenübertragung erfolgt verschlüsselt über TLS/SSL.'
      ]
    },
    {
      title: '11. Beschwerderecht',
      paragraphs: [
        'Wenn Sie der Ansicht sind, dass die Verarbeitung Ihrer personenbezogenen Daten gegen Datenschutzrecht verstößt, haben Sie das Recht, Beschwerde bei einer Aufsichtsbehörde einzulegen. In Spanien ist die zuständige Behörde die Agencia Española de Protección de Datos (AEPD), www.aepd.es.'
      ]
    },
    {
      title: '12. Änderungen dieser Datenschutzerklärung',
      paragraphs: [
        'Wir behalten uns das Recht vor, diese Datenschutzerklärung zu aktualisieren, um Änderungen der rechtlichen Anforderungen oder unserer Datenverarbeitungspraktiken widerzuspiegeln. Die aktuelle Version ist stets auf dieser Seite verfügbar.'
      ]
    },
    {
      title: '13. Kontakt',
      paragraphs: [
        'Bei Fragen zum Datenschutz oder zur Ausübung Ihrer Rechte kontaktieren Sie uns bitte unter: info@next-level.es.'
      ]
    },
  ]
};
